import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Plus, Minus, Trash, ShoppingBagOpen } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { useSettings } from '@/context/SettingsContext';
import { formatINR } from '@/lib/api';
import { CART } from '@/constants/testIds';
import Recommendations from '@/components/shop/Recommendations';

export default function CartDrawer() {
  const { items, increment, decrement, remove, subtotal, cartOpen, setCartOpen } = useCart();
  const { settings } = useSettings();
  const navigate = useNavigate();

  const threshold = settings.free_delivery_above || 299;
  const deliveryCharge = subtotal >= threshold || subtotal === 0 ? 0 : (settings.delivery_charge || 30);
  const platformFee = subtotal > 0 ? (Number(settings.platform_fee) || 0) : 0;
  const minOrder = settings.min_order_value || 199;
  const total = subtotal + deliveryCharge + platformFee;
  const aboveMin = subtotal >= minOrder;
  const remainingForFree = Math.max(0, threshold - subtotal);
  const progress = Math.min(100, (subtotal / threshold) * 100);

  const goCheckout = () => {
    setCartOpen(false);
    navigate('/checkout');
  };

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col bg-stone-50" data-testid={CART.drawer}>
        <SheetHeader className="px-5 py-4 border-b border-stone-200 bg-white">
          <SheetTitle className="font-heading text-xl text-stone-900 flex items-center gap-2">
            <ShoppingBagOpen size={22} weight="duotone" className="text-olive-700" /> Your Cart
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center" data-testid={CART.empty}>
            <div className="w-20 h-20 rounded-full bg-olive-50 grid place-items-center mb-4">
              <ShoppingBagOpen size={36} weight="duotone" className="text-olive-700" />
            </div>
            <h3 className="font-heading text-lg text-stone-900">Your cart is empty</h3>
            <p className="text-sm text-stone-500 mt-1">Add fresh groceries to get started.</p>
            <Button className="mt-5 rounded-full bg-stone-900 hover:bg-stone-800" onClick={() => { setCartOpen(false); navigate('/shop'); }} data-testid={CART.startShoppingButton}>
              Start Shopping
            </Button>
          </div>
        ) : (
          <>
            {subtotal < threshold && (
              <div className="px-5 py-3 bg-terracotta-50 border-b border-terracotta-100" data-testid={CART.freeDeliveryProgress}>
                <p className="text-xs text-stone-700">
                  Add <span className="font-semibold text-stone-900">{formatINR(remainingForFree)}</span> more for <span className="font-semibold text-olive-700">FREE delivery</span>
                </p>
                <div className="mt-2 h-1.5 bg-stone-200 rounded-full overflow-hidden">
                  <div className="h-full bg-olive-600 rounded-full transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2" data-testid={CART.itemsList}>
              {items.map((it) => (
                <div key={it.id} className="bg-white rounded-2xl p-3 flex gap-3 items-center border border-stone-100" data-testid={`${CART.item}-${it.id}`}>
                  <img src={it.image_url || '/placeholder.png'} alt={it.name} className="w-16 h-16 rounded-xl object-cover bg-stone-100" />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-stone-900 line-clamp-2">{it.name}</h4>
                    <p className="text-xs text-stone-500">{it.unit}</p>
                    <p className="text-sm font-semibold text-stone-900 mt-1">{formatINR(it.price * it.quantity)}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="flex items-center gap-2 bg-olive-50 rounded-full p-1">
                      <button onClick={() => decrement(it.id)} data-testid={`${CART.decrement}-${it.id}`} className="w-7 h-7 grid place-items-center rounded-full hover:bg-olive-100 text-olive-700">
                        <Minus size={14} weight="bold" />
                      </button>
                      <span className="text-sm font-semibold w-5 text-center" data-testid={`${CART.quantity}-${it.id}`}>{it.quantity}</span>
                      <button onClick={() => increment(it.id)} data-testid={`${CART.increment}-${it.id}`} className="w-7 h-7 grid place-items-center rounded-full hover:bg-olive-100 text-olive-700">
                        <Plus size={14} weight="bold" />
                      </button>
                    </div>
                    <button onClick={() => remove(it.id)} data-testid={`${CART.remove}-${it.id}`} className="text-stone-400 hover:text-red-600">
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
              ))}
              <div data-testid="cart-recommendations" className="pt-2">
                <Recommendations
                  cartIds={items.map((i) => i.id)}
                  title="Frequently added"
                  limit={4}
                  compact
                />
              </div>
            </div>

            <div className="border-t border-stone-200 bg-white px-5 py-4 space-y-2.5">
              <div className="flex justify-between text-sm">
                <span className="text-stone-500">Subtotal</span>
                <span className="font-semibold text-stone-900" data-testid={CART.subtotal}>{formatINR(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-stone-500">Delivery</span>
                <span className="font-semibold text-stone-900" data-testid={CART.deliveryCharge}>
                  {deliveryCharge === 0 ? <span className="text-olive-700">FREE</span> : formatINR(deliveryCharge)}
                </span>
              </div>
              {platformFee > 0 && (
                <div className="flex justify-between text-sm" data-testid="cart-platform-fee">
                  <span className="text-stone-500">Platform fee</span>
                  <span className="font-semibold text-stone-900">{formatINR(platformFee)}</span>
                </div>
              )}
              <div className="flex justify-between items-baseline pt-2 border-t border-stone-100">
                <span className="text-stone-900 font-heading text-base">Total</span>
                <span className="font-heading text-xl text-stone-900" data-testid={CART.total}>{formatINR(total)}</span>
              </div>
              {!aboveMin && (
                <p className="text-xs text-terracotta-700 bg-terracotta-50 px-3 py-2 rounded-lg" data-testid={CART.minOrderWarning}>
                  Minimum order value is {formatINR(minOrder)}.
                </p>
              )}
              <Button
                onClick={goCheckout}
                disabled={!aboveMin}
                data-testid={CART.checkoutButton}
                className="w-full h-12 rounded-full bg-stone-900 hover:bg-stone-800 text-base disabled:bg-stone-300"
              >
                Proceed to Checkout · {formatINR(total)}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
